import { useCallback, useEffect, useState } from 'react';
import { apiPost, operationsClient } from '@/lib/api';
import { operationsPricebookEndpoints } from '@/lib/api/endpoints';

export interface EstimateMembershipInfo {
  /** Frozen catalog plan identity, used to avoid adding the same sold plan twice. */
  planId?: string | null;
  planName: string | null;
  discountType: string | null;
  discountValue: number;
  discountTiers: MembershipDiscountTier[];
  eligibleLineItemTypes: string[];
  eligibleLineItemIds: string[];
  excludedLineItemIds: string[];
  /** Exact fee waivers are part of pricing policy, not billing metadata. */
  waivers?: MembershipFeeWaiver[];
  /**
   * ST parity — when the customer holds several active memberships, each plan's
   * OWN config (with its own eligibility/exclusion). The pricing preview prices
   * each line at the best discount across these; the top-level fields are a
   * display summary (joined names + best rate). Absent/single ⇒ single plan.
   */
  plans?: EstimateMembershipInfo[];
}

export interface MembershipFeeWaiver {
  lineItemId?: string | null;
  name?: string | null;
  waiverType?: string | null;
  maxPerPeriod?: number | null;
  value?: number | null;
  amount?: number | null;
}

export interface MembershipDiscountTier {
  label?: string | null;
  type: string;
  value: number;
  appliesTo?: string[];
  specificItemIds?: string[];
}

export function useEstimateMembershipInfo(contactId: string | null) {
  const [membershipInfo, setMembershipInfo] = useState<EstimateMembershipInfo | null>(null);
  // Settled = the membership context is POSITIVELY KNOWN: the lookup resolved
  // successfully (member or confirmed non-member) for the current contact. Every
  // other state — no contact yet, in flight, or errored — is unresolved, so
  // membership-conditioned logic (upsell auto-insert) stays off until then and
  // never evaluates an unknown customer as 'NONE' (NOT-306).
  const [isSettled, setIsSettled] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setAttempt((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!contactId) {
      setMembershipInfo(null);
      setIsSettled(false);
      setHasError(false);
      return;
    }

    let cancelled = false;
    // Clear the previous customer's data immediately on switch — otherwise the
    // old membership lingers until the new lookup resolves and could misprice
    // (or submit wrong totals for) the new customer (NOT-306).
    setMembershipInfo(null);
    setIsSettled(false);
    setHasError(false);

    // ST parity: a customer can hold SEVERAL active memberships. Fetch them all
    // (no limit) and MERGE their discount configs so the client preview reflects
    // every plan's member rates. The backend prices the best rate per line
    // authoritatively on save; this merge is only the immediate on-screen preview.
    apiPost<{
      data: { activePlanSnapshots: Record<string, unknown>[] };
    }>(operationsClient, operationsPricebookEndpoints.membershipPricingContext(), {
      contactId,
      planIds: [],
    })
      .then((res) => {
        if (cancelled) return;

        const snaps = res?.data?.activePlanSnapshots ?? [];
        if (snaps.length === 0) {
          setMembershipInfo(null);
          setIsSettled(true);
          return;
        }

        setMembershipInfo(mergeMembershipInfo(snaps));
        setIsSettled(true);
        setHasError(false);
      })
      .catch(() => {
        if (!cancelled) {
          // A failed lookup is NOT a confirmed non-member — leaving it
          // "unsettled" keeps membership-conditioned logic (upsell auto-insert)
          // disabled rather than evaluating a real member as 'NONE' (NOT-306).
          setMembershipInfo(null);
          setIsSettled(false);
          setHasError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attempt, contactId]);

  return { membershipInfo, isSettled, hasError, retry };
}

/** Map ONE plan snapshot to its own membership-info config (single-plan logic). */
function snapToInfo(snap: Record<string, unknown>): EstimateMembershipInfo {
  const discountPercent = toNumber(snap['discountPercent']);
  const legacyValue = toNumber(snap['discountValue']);
  const legacyType = typeof snap['discountType'] === 'string' ? snap['discountType'] : null;
  return {
    planId:
      typeof snap['id'] === 'string'
        ? snap['id']
        : typeof snap['_id'] === 'string'
          ? snap['_id']
          : null,
    planName: (snap['name'] as string) ?? null,
    discountType: discountPercent > 0 ? 'PERCENT' : legacyType,
    discountValue: discountPercent > 0 ? discountPercent : legacyValue,
    discountTiers: toDiscountTiers(snap['discountTiers']),
    eligibleLineItemTypes: toStringArray(
      snap['eligibleLineItemTypes'] ?? snap['applicableItemTypes'],
    ),
    eligibleLineItemIds: toStringArray(snap['eligibleLineItemIds']),
    excludedLineItemIds: toStringArray(snap['excludedLineItemIds']),
    waivers: toWaivers(snap['waivers']),
  };
}

/**
 * Combine one or more active memberships' plan snapshots. For ONE membership this
 * is exactly that plan's config. For several, `plans[]` carries each plan's OWN
 * config (with its own eligibility) so the pricing preview prices each line at the
 * best plan per line — mirroring the backend orchestrator, NOT a lossy flattened
 * pseudo-plan. The top-level fields are only a display summary (joined names + the
 * best blanket rate). The backend is authoritative on save.
 */
function mergeMembershipInfo(snaps: Record<string, unknown>[]): EstimateMembershipInfo | null {
  return combineEstimateMembershipInfo(snaps.map(snapToInfo));
}

/**
 * Build one pricing context from active and/or prospective plans. The pricing
 * engine evaluates `plans[]` independently and takes the best benefit per line;
 * it never stacks discounts. Frozen plan identity prevents a cart containing an
 * already-active plan from duplicating that same plan in the preview.
 */
export function combineEstimateMembershipInfo(
  infos: Array<EstimateMembershipInfo | null | undefined>,
): EstimateMembershipInfo | null {
  const flattened = infos.flatMap((info) => {
    if (!info) return [];
    return info.plans && info.plans.length > 0 ? info.plans : [info];
  });
  const seenPlanIds = new Set<string>();
  const perPlan = flattened.filter((info) => {
    const planId = info.planId?.trim();
    if (!planId) return true;
    if (seenPlanIds.has(planId)) return false;
    seenPlanIds.add(planId);
    return true;
  });

  if (perPlan.length === 0) return null;
  if (perPlan.length === 1) return perPlan[0];

  const names = perPlan.map((p) => p.planName).filter((n): n is string => !!n);
  const maxPercent = Math.max(
    0,
    ...perPlan.map((p) => (p.discountType === 'PERCENT' ? p.discountValue : 0)),
  );

  return {
    // Display summary only — the real pricing runs per-plan via `plans` below.
    planName: names.length > 0 ? names.join(' + ') : null,
    discountType: maxPercent > 0 ? 'PERCENT' : null,
    discountValue: maxPercent,
    discountTiers: [],
    eligibleLineItemTypes: [],
    eligibleLineItemIds: [],
    excludedLineItemIds: [],
    waivers: [],
    plans: perPlan,
  };
}

function toWaivers(value: unknown): MembershipFeeWaiver[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((waiver): waiver is Record<string, unknown> =>
      Boolean(waiver && typeof waiver === 'object' && !Array.isArray(waiver)),
    )
    .map((waiver) => ({
      lineItemId: typeof waiver['lineItemId'] === 'string' ? waiver['lineItemId'] : null,
      name: typeof waiver['name'] === 'string' ? waiver['name'] : null,
      waiverType: typeof waiver['waiverType'] === 'string' ? waiver['waiverType'] : null,
      maxPerPeriod: waiver['maxPerPeriod'] == null ? null : toNumber(waiver['maxPerPeriod']),
      value: waiver['value'] == null ? null : toNumber(waiver['value']),
      amount: waiver['amount'] == null ? null : toNumber(waiver['amount']),
    }));
}

function toNumber(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

function toDiscountTiers(value: unknown): MembershipDiscountTier[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((tier): tier is Record<string, unknown> => typeof tier === 'object' && tier !== null)
    .map((tier) => ({
      label: typeof tier['label'] === 'string' ? tier['label'] : null,
      type: typeof tier['type'] === 'string' ? tier['type'] : 'PERCENT',
      value: toNumber(tier['value']),
      appliesTo: toStringArray(tier['appliesTo']),
      specificItemIds: toStringArray(tier['specificItemIds']),
    }));
}
